import { Component, DestroyRef, inject, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { MatTab, MatTabContent, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { TableType } from '../../models/shipment';
import { RefreshTab, UtilsTabsService } from './services/utils-tabs.service';
import { catchError, filter, NEVER, startWith, switchMap, tap } from 'rxjs';
import { Tab } from './models/utils-tabs';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { UtilsTabsSkeletonComponent } from './components/tabs-skeleton/utils-tabs-skeleton.component';
import { MatDialog } from '@angular/material/dialog';
import { TabsConfiguratorComponent } from './components/tabs-configurator/tabs-configurator.component';
import { UtilsTableComponent } from './components/utils-table/utils-table.component';
import { UtilsTableService } from './services/utils-table.service';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-utils-tabs',
  standalone: true,
  templateUrl: './utils-tabs.component.html',
  imports: [
    MatTabGroup,
    MatTab,
    UtilsTabsSkeletonComponent,
    UtilsTableComponent,
    NgTemplateOutlet,
    MatTabContent,
    MatTabLabel,
    CdkDropList,
    CdkDrag,
    NgClass,
    MatIcon,
    CdkDragHandle
  ],
  styleUrl: './utils-tabs.component.scss'
})
export class UtilsTabsComponent implements OnChanges, OnDestroy {
  @ViewChild('tabGroup') tabGroup?: MatTabGroup;
  @Input() type: TableType | null = null;

  tabs: Tab[] | null = null;

  private currentTabIndex = 0;
  private utilsTabsService = inject(UtilsTabsService);
  private utilsTableService = inject(UtilsTableService);
  private matDialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private matSnackbar = inject(MatSnackBar);

  ngOnChanges(changes: SimpleChanges) {
    if (changes['type'].currentValue) {
      this.utilsTabsService
        .getTabsRefresh()
        .pipe(
          startWith(null),
          switchMap(res => this.getTabs(res)),
          tap(tabs => (this.tabs = tabs)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }

  ngOnDestroy() {
    this.utilsTableService.resetAllFilters();
  }

  onTabIndexChange(index: number) {
    if (index === this.numberOfTabs() && this.type !== 'Archive') {
      this.tabGroup!.selectedIndex = this.currentTabIndex;
      this.matDialog
        .open(TabsConfiguratorComponent, {
          height: '90vh',
          maxWidth: '770px',
          width: '100%',
          disableClose: true,
          data: { order: index + 1, type: this.type }
        })
        .afterClosed()
        .pipe(
          filter(response => response === 'Created'),
          tap(() => this.utilsTabsService.setTabsRefresh('selectLastTab')),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    } else {
      this.currentTabIndex = index;
    }
  }

  onTabDrop({ previousIndex, currentIndex, container }: CdkDragDrop<Tab[], any>) {
    if (previousIndex === currentIndex) {
      return;
    }
    const tabs = [...container.data];
    const nextOnTheRight = tabs[currentIndex];

    if (nextOnTheRight.predefined) {
      this.matSnackbar.open('You can only change the order between your tabs', 'x');
      return;
    }

    moveItemInArray(tabs, previousIndex, currentIndex);
    const maxPredefinedOrder = tabs
      .filter(({ predefined }) => predefined)
      .reduce((max, { order }) => Math.max(max, order ?? -1), -1);
    let customOrder = maxPredefinedOrder + 1;
    this.tabs = tabs.map(tab => (tab.predefined ? tab : { ...tab, order: customOrder++ }));
    this.updateTabOrders();
  }

  private getTabs(refreshTab: RefreshTab) {
    const archive = this.type === 'Archive';
    return this.utilsTabsService
      .getTabs({
        type: archive ? null : this.type,
        archive
      })
      .pipe(
        tap(() => {
          if (refreshTab === 'selectLastTab') {
            this.tabGroup!.selectedIndex = this.numberOfTabs();
          } else if (typeof refreshTab === 'number') {
            this.tabGroup!.selectedIndex = refreshTab;
          }
        })
      );
  }

  private updateTabOrders() {
    const customTabOrders = this.tabs!.filter(({ predefined }) => !predefined).map(({ order, id: tabId }) => ({
      order,
      tabId
    }));
    this.utilsTabsService
      .reorderTabs(customTabOrders)
      .pipe(
        tap(() =>
          this.matSnackbar.open('Tabs have been successfully reordered', 'x', { panelClass: 'snackbar-green' })
        ),
        catchError(err => {
          console.error(err);
          this.matSnackbar.open('Could not update tab order. Please try again.', 'x');
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private numberOfTabs() {
    return this.tabGroup!._allTabs.length - 1;
  }
}
